/**
 * Manual stop RPCs (vehixdb-prod) and the address-validate edge function.
 *
 * All calls go through the public-schema client: each prod.* function has a
 * same-name public.* wrapper, matching upsert_technician_route et al.
 * Customer contact details are only ever read through getManualStopContacts —
 * every read is audit-logged server-side, so call it on demand, never in a loop.
 */

import type { PostgrestError } from '@supabase/supabase-js';
import { supabase } from './client';
import type { ManualStopSubmission } from '@/lib/validation/manualStopValidation';
import type {
  AddressValidationRequest,
  AddressValidationResult,
  ContactAccessContext,
  ManualStopContact,
  ManualStopRpcErrorCode,
  SelectedRecallRow,
  StageManualStopsPayload
} from '@/types/manualStops';

// =============================================================================
// Errors
// =============================================================================

const ERROR_CODES: readonly ManualStopRpcErrorCode[] = ['PT401', 'PT404', 'PT422'];

const USER_MESSAGES: Record<ManualStopRpcErrorCode, string> = {
  PT401: "You don't have permission to do that.",
  PT404: 'That stop no longer exists.',
  PT422: 'Some details need fixing before this can be saved.'
};

const CONTACT_FIELDS = ['first_name', 'last_name', 'phone_e164', 'email', 'preferred_contact'];

/** Specific messages for server checks the form can't fully run itself. */
const FIELD_MESSAGES: Record<string, string> = {
  duplicate_stop: 'One of these vehicles is already on the stop list for that campaign.',
  vin: "One of the VINs doesn't pass the check-digit test.",
  vehicle_year: "One of the vehicles' model year isn't valid.",
  manual_stop_reason: "One of the reasons isn't available for this kind of stop.",
  custom_reason: 'Check the custom reason on each vehicle marked "Other".',
  campaigns: 'Campaigns can only be attached to a recall.',
  too_long: 'One of the fields is too long.',
  permission: "You don't have permission to add manual stops."
};

/** Address-derived fields have no input of their own; errors show under the address. */
const ADDRESS_DERIVED = new Set(['location.lat', 'location.lng']);

/**
 * Map a server DETAIL to a form path. Location fields already come dotted
 * ("location.city"); contact fields come bare ("phone_e164"); vehicle fields carry no
 * index, so they surface as a form-level message.
 */
function toFormPath(field: string | undefined): string | undefined {
  if (!field) return undefined;
  if (ADDRESS_DERIVED.has(field)) return 'location.address';
  if (field.startsWith('location.') || field === 'dispatcher_note') return field;
  if (CONTACT_FIELDS.includes(field)) return `contact.${field}`;
  return undefined;
}

export class ManualStopRpcError extends Error {
  readonly code: ManualStopRpcErrorCode | 'unknown';
  /** Server-reported field (DETAIL), e.g. "vin" or "duplicate_stop". */
  readonly field: string | undefined;
  /** Matching form path for inline errors, when known. */
  readonly formPath: string | undefined;
  /** The server's own explanation (RAISE message), shown next to the field. */
  readonly serverMessage: string;
  /** HTTP status PostgREST returned (PTnnn should map to nnn; not yet confirmed live). */
  readonly httpStatus: number | undefined;

  constructor(error: PostgrestError, httpStatus?: number) {
    const code = ERROR_CODES.find(c => c === error.code);
    const field = error.details?.trim() || undefined;
    const fieldMessage = field ? FIELD_MESSAGES[field] : undefined;
    super(fieldMessage ?? (code ? USER_MESSAGES[code] : 'Something went wrong. Please try again.'));
    this.name = 'ManualStopRpcError';
    this.code = code ?? 'unknown';
    this.field = field;
    this.formPath = toFormPath(field);
    this.serverMessage = error.message;
    this.httpStatus = httpStatus;
  }
}

let loggedPtStatus = false;

/** Dev only: the backend asked for the observed HTTP status of the first PT4xx error. */
function reportPtStatusOnce(err: ManualStopRpcError) {
  if (!import.meta.env.DEV || loggedPtStatus || err.code === 'unknown') return;
  loggedPtStatus = true;
  // eslint-disable-next-line no-console -- requested by the backend team (handoff §1.1)
  console.info(`[manualStops] ${err.code} arrived as HTTP ${err.httpStatus ?? 'unknown'}`);
}

function rpcError(error: PostgrestError, status: number): ManualStopRpcError {
  const err = new ManualStopRpcError(error, status);
  reportPtStatusOnce(err);
  return err;
}

// =============================================================================
// Payload mapping
// =============================================================================

/** Build the RPC payload from a schema-validated submission. */
export function toStagePayload(submission: ManualStopSubmission): StageManualStopsPayload {
  const { location } = submission;
  return {
    stop_kind: submission.stop_kind,
    location: {
      address: location.address,
      city: location.city,
      region: location.region,
      lat: location.lat,
      lng: location.lng,
      ...(location.address_line2 && { address_line2: location.address_line2 }),
      ...(location.postal_code && { postal_code: location.postal_code }),
      ...(submission.stop_kind === 'business' && {
        name: submission.location.name,
        ...(submission.location.phone && { phone: submission.location.phone })
      })
    },
    ...(submission.stop_kind === 'customer' && {
      contact: {
        first_name: submission.contact.first_name,
        last_name: submission.contact.last_name,
        phone_e164: submission.contact.phone_e164,
        preferred_contact: submission.contact.preferred_contact,
        ...(submission.contact.email && { email: submission.contact.email })
      }
    }),
    ...(submission.address_validation && { address_validation: submission.address_validation }),
    ...(submission.dispatcher_note && { dispatcher_note: submission.dispatcher_note }),
    vehicles: submission.vehicles.map(v => ({
      vin: v.vin,
      manual_stop_reason: v.manual_stop_reason,
      campaigns: v.campaigns.map(c => ({
        campaign_number: c.campaign_number,
        ...(c.recall_title && { recall_title: c.recall_title })
      })),
      ...(v.vehicle_year && { vehicle_year: v.vehicle_year }),
      ...(v.vehicle_make && { vehicle_make: v.vehicle_make }),
      ...(v.vehicle_model && { vehicle_model: v.vehicle_model }),
      // The RPC rejects custom_reason on any reason other than 'other'.
      ...(v.manual_stop_reason === 'other' && v.custom_reason && { custom_reason: v.custom_reason })
    }))
  };
}

// =============================================================================
// RPCs
// =============================================================================

/**
 * Stage a business or customer stop (one row per vehicle × campaign) in one
 * transaction. Returns the created prod.selected_recalls rows.
 */
export async function stageManualStops(
  submission: ManualStopSubmission
): Promise<SelectedRecallRow[]> {
  const { data, error, status } = await supabase.rpc('stage_manual_stops_v1', {
    p_payload: toStagePayload(submission)
  });
  if (error) throw rpcError(error, status);
  // Generated types point this at the legacy public.selected_recalls — hand-typed instead.
  return (Array.isArray(data) ? data : [data]) as unknown as SelectedRecallRow[];
}

/**
 * Read customer contact details. Ids the caller may not see are omitted, so the
 * result can be shorter than the input. Logged to prod.pii_access_log.
 */
export async function getManualStopContacts(
  contactIds: string[],
  context: ContactAccessContext
): Promise<ManualStopContact[]> {
  const ids = Array.from(new Set(contactIds.filter(Boolean)));
  if (ids.length === 0) return [];
  const results: ManualStopContact[] = [];
  // The RPC accepts at most 200 ids per call.
  for (let i = 0; i < ids.length; i += 200) {
    const { data, error, status } = await supabase.rpc('get_manual_stop_contacts', {
      p_contact_ids: ids.slice(i, i + 200),
      p_context: context
    });
    if (error) throw rpcError(error, status);
    results.push(...((data ?? []) as unknown as ManualStopContact[]));
  }
  return results;
}

/** Set or clear the dispatcher note on a staged stop or an assignment. */
export async function setDispatcherNote(
  target: 'selected_recall' | 'assignment',
  id: string,
  note: string | undefined
): Promise<void> {
  const { error, status } = await supabase.rpc('set_dispatcher_note', {
    p_target: target,
    p_id: id,
    p_note: note ?? null
  });
  if (error) throw rpcError(error, status);
}

// =============================================================================
// Address validation (edge function)
// =============================================================================

const UNAVAILABLE: AddressValidationResult = {
  status: 'unavailable',
  standardized: null,
  geocode: null,
  dpv: null,
  residential: null,
  business: null,
  vacant: null,
  corrected_components: [],
  missing_components: [],
  response_id: null
};

/**
 * Validate a US address against USPS data (Google Address Validation, CASS mode).
 * Never throws: if the service can't be reached the result is `unavailable`, and the
 * dispatcher can still save after confirming the address themselves.
 */
export async function validateAddress(
  request: AddressValidationRequest
): Promise<AddressValidationResult> {
  try {
    const { data, error } = await supabase.functions.invoke('address-validate', {
      body: request
    });
    if (error || !data || typeof data !== 'object' || !('status' in data)) {
      // eslint-disable-next-line no-console -- degraded mode must be visible when debugging
      if (error) console.warn('[addressValidation] unavailable:', error.message);
      return UNAVAILABLE;
    }
    // Responses arrive in a { success: true, ... } envelope; keep only the result fields.
    const result = { ...(data as AddressValidationResult & { success?: boolean }) };
    delete result.success;
    return result;
  } catch (err) {
    // eslint-disable-next-line no-console -- degraded mode must be visible when debugging
    console.warn('[addressValidation] unavailable:', err);
    return UNAVAILABLE;
  }
}
